import React, {
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  Dispatch,
  SetStateAction,
  type CSSProperties
} from 'react'
import { ColumnDef, PaginationState, Row } from '@tanstack/react-table'
import { useLocale } from 'src/lib/hooks/useLocal'
import { ScrollMode } from 'src/components/common/table/table-types'
import GenericTableV2 from 'src/components/common/table/GenericTableV2'
import { TABLE_PAGINATION_FOOTER_HEIGHT } from 'src/components/common/table/pagination/TablePagination'

interface PreviewTableRow {
  [key: string]: string | null
}

interface DynamicSegmentPreviewTableProps {
  previewData: PreviewTableRow[]
  loading: boolean
  pagination: {
    paginationState: PaginationState
    setPaginationState: Dispatch<SetStateAction<PaginationState>>
  }
  dimensionIds: string[]
  dimensionNames: Record<string, string>
  knownTotalRowCount?: number
  backgroundTransparent?: boolean
  isDetailPanelVisible?: boolean
}

export type DynamicSegmentPreviewTableHandle = {
  snapshotLayout: () => void
  clearLayoutSnapshot: () => void
}

const DETAILS_PANEL_WIDTH = 440
const INDEX_COLUMN_WIDTH = 80
const DATA_COLUMN_MIN_WIDTH = 180

const cellRow = 'box-border flex h-[44px] min-w-0 items-center px-2'

function isPhoneDimension(dimensionId: string, dimensionName: string) {
  return `${dimensionId} ${dimensionName}`.toLowerCase().includes('phone')
}

function snapshotTableLayout(tableEl: HTMLTableElement, columnDefs: ColumnDef<PreviewTableRow>[]) {
  const tableWidth = tableEl.getBoundingClientRect().width
  const headerCells = tableEl.querySelectorAll('thead tr th')
  const columnWidths: Record<string, number> = {}
  let dataColIndex = 0
  headerCells.forEach(cell => {
    const colDef = columnDefs[dataColIndex]
    if (colDef?.id) {
      columnWidths[colDef.id] = cell.getBoundingClientRect().width
    }
    dataColIndex += 1
  })
  return { tableWidth, columnWidths }
}

function getPreviewColumnStyle(
  columnId: string,
  frozenWidths?: Record<string, number>
): CSSProperties | undefined {
  if (columnId === '__index__') {
    return { width: INDEX_COLUMN_WIDTH, minWidth: INDEX_COLUMN_WIDTH, maxWidth: INDEX_COLUMN_WIDTH }
  }
  const lockedWidth = frozenWidths?.[columnId]
  if (lockedWidth != null) {
    const width = Math.max(lockedWidth, DATA_COLUMN_MIN_WIDTH)
    return { width, minWidth: width, maxWidth: width }
  }
  return { minWidth: DATA_COLUMN_MIN_WIDTH }
}

const DynamicSegmentPreviewTable = forwardRef<DynamicSegmentPreviewTableHandle, DynamicSegmentPreviewTableProps>(
  (
    {
      previewData,
      loading,
      pagination,
      dimensionIds,
      dimensionNames,
      knownTotalRowCount,
      backgroundTransparent = false,
      isDetailPanelVisible = false
    },
    ref
  ) => {
    const { t } = useLocale()
    const scrollRegionRef = useRef<HTMLDivElement>(null)
    const tableColumnsRef = useRef<ColumnDef<PreviewTableRow>[]>([])
    const [frozenTableWidth, setFrozenTableWidth] = useState<number | null>(null)
    const [frozenColumnWidths, setFrozenColumnWidths] = useState<Record<string, number> | null>(null)
    const pageIndex = pagination.paginationState.pageIndex
    const pageSize = pagination.paginationState.pageSize

    const tableColumns: ColumnDef<PreviewTableRow>[] = useMemo(() => {
      const indexColumn: ColumnDef<PreviewTableRow> = {
        id: '__index__',
        header: '#',
        minSize: INDEX_COLUMN_WIDTH,
        size: INDEX_COLUMN_WIDTH,
        cell: ({ row }: { row: Row<PreviewTableRow> }) => {
          const index = pageIndex * pageSize + row.index + 1
          return (
            <div className={`${cellRow} w-[80px] min-w-[80px] shrink-0`}>
              <p className='text-text-xs-regular text-zinc-400'>{index}</p>
            </div>
          )
        }
      }

      const dataColumns = dimensionIds.map(dimensionId => {
        const dimensionName = dimensionNames[dimensionId] || dimensionId
        const valueClassName = isPhoneDimension(dimensionId, dimensionName)
          ? 'truncate text-text-sm-medium text-zinc-800'
          : 'truncate text-text-xs-regular text-zinc-600'

        return {
          id: dimensionId,
          header: () => (
            <span className='line-clamp-1 min-w-0 truncate'>{dimensionName}</span>
          ),
          minSize: DATA_COLUMN_MIN_WIDTH,
          meta: { headerCellClassName: 'overflow-hidden' },
          cell: ({ row }: { row: Row<PreviewTableRow> }) => {
            const value = row.original[dimensionId]
            return (
              <div className={cellRow}>
                <p className={valueClassName}>{value || '-'}</p>
              </div>
            )
          }
        }
      })

      return [indexColumn, ...dataColumns]
    }, [dimensionIds, dimensionNames, pageIndex, pageSize])

    tableColumnsRef.current = tableColumns

    const captureSnapshot = () => {
      const tableEl = scrollRegionRef.current?.querySelector('table')
      if (!tableEl) return
      const snapshot = snapshotTableLayout(tableEl, tableColumnsRef.current)
      setFrozenTableWidth(snapshot.tableWidth)
      setFrozenColumnWidths(snapshot.columnWidths)
    }

    useImperativeHandle(ref, () => ({
      snapshotLayout: captureSnapshot,
      clearLayoutSnapshot: () => {
        setFrozenTableWidth(null)
        setFrozenColumnWidths(null)
      }
    }))

    // Panel starts open: measure the full-screen stretched table, then inset the scroll viewport.
    useLayoutEffect(() => {
      if (!isDetailPanelVisible || frozenTableWidth != null) return
      captureSnapshot()
    }, [isDetailPanelVisible, frozenTableWidth, previewData, dimensionIds])

    if (dimensionIds.length === 0) {
      return (
        <div className='flex h-full flex-col items-center justify-center p-8'>
          <p className='text-text-sm-regular text-zinc-500'>{t('segment_creation.select_columns_to_preview')}</p>
        </div>
      )
    }

    const resolvedTotalRows = knownTotalRowCount ?? 0
    const resolvedPageCount =
      knownTotalRowCount != null ? Math.max(1, Math.ceil(knownTotalRowCount / pageSize)) : -1
    const hasNextPage =
      knownTotalRowCount != null
        ? (pageIndex + 1) * pageSize < knownTotalRowCount
        : previewData.length >= pageSize

    const hasFreeze = isDetailPanelVisible && frozenTableWidth != null && frozenColumnWidths != null
    const tableMinWidth = INDEX_COLUMN_WIDTH + dimensionIds.length * DATA_COLUMN_MIN_WIDTH

    return (
      <div
        className='flex h-full min-h-0 min-w-0 flex-1 flex-col'
        style={{ transform: 'translateZ(0)', '--sidebar-width': '0px' } as React.CSSProperties}
      >
        <div
          ref={scrollRegionRef}
          className={`min-h-0 min-w-0 flex-1 overflow-y-auto scrollbar-thin ${
            hasFreeze ? 'overflow-x-scroll' : 'overflow-x-auto'
          }`}
          style={hasFreeze ? { width: `calc(100% - ${DETAILS_PANEL_WIDTH}px)` } : undefined}
        >
          <div className='min-w-full px-12'>
            <GenericTableV2
              backgroundTransparent={backgroundTransparent}
              data={previewData}
              columns={tableColumns}
              tableLayout='auto'
              stickyHeaderTop={0}
              scrollInParent
              cellBorders='horizontal'
              tableStyle={
                hasFreeze
                  ? {
                      minWidth: Math.max(frozenTableWidth, tableMinWidth),
                      width: Math.max(frozenTableWidth, tableMinWidth)
                    }
                  : { width: '100%', minWidth: tableMinWidth }
              }
              getColumnStyle={columnId =>
                getPreviewColumnStyle(columnId, hasFreeze ? frozenColumnWidths : undefined)
              }
              pagination={{
                pageIndex,
                pageSize,
                totalRows: resolvedTotalRows,
                hasNextPage,
                onPaginationChange: pagination.setPaginationState,
                pageSizeOptions: [
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' }
                ],
                pageCount: resolvedPageCount,
                fixedBottom: true,
                contentLeftPadding: 48,
                contentRightPadding: isDetailPanelVisible ? DETAILS_PANEL_WIDTH + 48 : 48
              }}
              isLoading={loading}
              scrollMode={ScrollMode.PAGINATION}
            />
          </div>
        </div>
        <div className='shrink-0' style={{ height: TABLE_PAGINATION_FOOTER_HEIGHT }} aria-hidden />
      </div>
    )
  }
)

DynamicSegmentPreviewTable.displayName = 'DynamicSegmentPreviewTable'

export default DynamicSegmentPreviewTable
